import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { z } from 'zod';
import { appRoute, type RouterContext } from '@/app/layout-routes';
import { requirePermissions } from '@/app/route-guards';
import { filterParam } from '@/lib/url-state';
import { sourceQuery } from './api/client';
import { ALERT_TYPES, DIMENSION_IDS, METRIC_IDS, SEVERITIES } from './api/schemas';
import { analyticsSearchSchema, TREND_METRICS } from './filters';
import { CHURN_GRAINS, FUNNEL_BY, HOUR_METRICS, LTV_COHORTS, PLAYER_GRAINS, RANKING_TABS } from './player-params';

const guard = ({ context }: { context: RouterContext }) => requirePermissions(context, 'VIEW_LIST_GAMES');
// Every analytics page first learns where its numbers come from (API, interim legacy, or none).
const loader = ({ context }: { context: RouterContext }) => {
  void context.queryClient.prefetchQuery(sourceQuery);
};

const list = <T extends z.ZodType>(item: T, max: number) =>
  z
    .preprocess((v) => (v === undefined || v === null ? undefined : Array.isArray(v) ? v : [v]), z.array(item).max(max))
    .optional()
    .catch(undefined);

export const SPLIT_VALUES = ['total', 'gameId', 'operatorId', 'brandId'] as const;

export const overviewSearchSchema = analyticsSearchSchema.extend({
  metric: filterParam.enum(TREND_METRICS),
  split: filterParam.enum(SPLIT_VALUES),
  /** The games card's rows: titles (the default) or every RTP variant. */
  games: filterParam.enum(['titles', 'variants']),
});

export const analyticsOverviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics',
  validateSearch: overviewSearchSchema,
  beforeLoad: guard,
  loader,
  component: lazyRouteComponent(() => import('./pages/overview-page'), 'OverviewPage'),
});

export const analyticsGamesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics/games',
  validateSearch: analyticsSearchSchema.extend({ q: filterParam.string() }),
  beforeLoad: guard,
  loader,
  component: lazyRouteComponent(() => import('./pages/games-page'), 'GamesPage'),
});

export const analyticsGameRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics/games/$titleKey',
  validateSearch: analyticsSearchSchema.extend({
    variant: filterParam.number(),
    split: filterParam.enum(['operatorId', 'brandId']),
  }),
  beforeLoad: guard,
  loader,
  staticData: { crumbs: (params) => [{ label: params.titleKey ?? '' }] },
  component: lazyRouteComponent(() => import('./pages/game-detail-page'), 'GameDetailPage'),
});

export const analyticsOperatorsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics/operators',
  validateSearch: analyticsSearchSchema,
  beforeLoad: guard,
  loader,
  component: lazyRouteComponent(() => import('./pages/operators-page'), 'OperatorsPage'),
});

export const analyticsOperatorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics/operators/$operatorId',
  validateSearch: analyticsSearchSchema.extend({
    metric: filterParam.enum(TREND_METRICS),
    split: filterParam.enum(['total', 'gameId', 'brandId']),
  }),
  beforeLoad: guard,
  loader,
  staticData: { crumbs: (params) => [{ label: `Operator ${params.operatorId ?? ''}` }] },
  component: lazyRouteComponent(() => import('./pages/operator-detail-page'), 'OperatorDetailPage'),
});

export const analyticsPlayersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics/players',
  validateSearch: analyticsSearchSchema.extend({
    grain: filterParam.enum(PLAYER_GRAINS),
    hours: filterParam.enum(HOUR_METRICS),
  }),
  beforeLoad: guard,
  loader,
  component: lazyRouteComponent(() => import('./pages/players-page'), 'PlayersPage'),
});

export const analyticsRetentionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics/players/retention',
  validateSearch: analyticsSearchSchema.extend({
    cohort: filterParam.enum(PLAYER_GRAINS),
    periods: filterParam.number(),
    churn: filterParam.enum(CHURN_GRAINS),
    inactiveDays: filterParam.number(),
  }),
  beforeLoad: guard,
  loader,
  component: lazyRouteComponent(() => import('./pages/retention-page'), 'RetentionPage'),
});

export const analyticsFunnelRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics/players/funnel',
  validateSearch: analyticsSearchSchema.extend({ by: filterParam.enum(FUNNEL_BY) }),
  beforeLoad: guard,
  loader,
  component: lazyRouteComponent(() => import('./pages/funnel-page'), 'FunnelPage'),
});

export const analyticsPlayerValueRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics/players/value',
  validateSearch: analyticsSearchSchema.extend({
    cohort: filterParam.enum(LTV_COHORTS),
    days: filterParam.number(),
    gap: filterParam.number(),
  }),
  beforeLoad: guard,
  loader,
  component: lazyRouteComponent(() => import('./pages/player-value-page'), 'PlayerValuePage'),
});

export const analyticsPlayerRiskRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics/players/risk',
  validateSearch: analyticsSearchSchema.extend({ tab: filterParam.enum(RANKING_TABS), page: filterParam.number() }),
  // Individual wallets: the analytics bit and the players list bit.
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_GAMES', 'VIEW_LIST_PLAYERS'),
  loader,
  component: lazyRouteComponent(() => import('./pages/player-risk-page'), 'PlayerRiskPage'),
});

export const analyticsPlayerWalletRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics/players/wallet/$operatorId/$playerId',
  validateSearch: analyticsSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_GAMES', 'VIEW_SINGLE_PLAYERS'),
  loader,
  staticData: { crumbs: (params) => [{ label: `Player ${params.playerId ?? ''}` }] },
  component: lazyRouteComponent(() => import('./pages/player-profile-page'), 'PlayerProfilePage'),
});

export const exploreSearchSchema = analyticsSearchSchema.extend({
  metrics: list(z.enum(METRIC_IDS), 6),
  dims: list(z.enum(DIMENSION_IDS), 2),
  view: filterParam.enum(['chart', 'table']),
});

export const analyticsExploreRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics/explore',
  validateSearch: exploreSearchSchema,
  beforeLoad: guard,
  loader,
  component: lazyRouteComponent(() => import('./pages/explore-page'), 'ExplorePage'),
});

export const analyticsReportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics/reports',
  validateSearch: analyticsSearchSchema,
  beforeLoad: guard,
  loader,
  component: lazyRouteComponent(() => import('./pages/reports-page'), 'ReportsPage'),
});

export const analyticsAlertsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'analytics/alerts',
  validateSearch: analyticsSearchSchema.extend({
    severity: filterParam.enum(SEVERITIES),
    type: filterParam.enum(ALERT_TYPES),
    /** The alert open in the detail pane (spec 2026-10-27 §5). */
    alert: filterParam.string(),
  }),
  beforeLoad: guard,
  loader,
  component: lazyRouteComponent(() => import('./pages/alerts-page'), 'AlertsPage'),
});

/** Routes for this feature, attached to `appRoute`; `app/routeTree.ts` spreads them in. */
export const analyticsRoutes = [
  analyticsOverviewRoute,
  analyticsGamesRoute,
  analyticsGameRoute,
  analyticsOperatorsRoute,
  analyticsOperatorRoute,
  analyticsPlayersRoute,
  analyticsRetentionRoute,
  analyticsFunnelRoute,
  analyticsPlayerValueRoute,
  analyticsPlayerRiskRoute,
  analyticsPlayerWalletRoute,
  analyticsExploreRoute,
  analyticsReportsRoute,
  analyticsAlertsRoute,
] as const;
